import React, { useState } from 'react';
import { Spinner } from 'reactstrap';
import { ThemeLink } from '../shared/ThemeLink/ThemeLink';
import { ProductProps } from '../../types/productTypes';
import { Image } from '../shared/Image/Image';
import { RemoveItemButton } from '../RemoveItemButton/RemoveItemButton';
// import { ProductWatermark } from "./ProductItemWatermark";
import { getProductImages } from '../../pages/SingleProduct/lib/helpers/getProductImages';
import { useHistory } from 'react-router-dom';
import { useProductSingleHelpers } from '../lib/useProductSingleHelpers';
import { WishListButton } from '../shared/WishListButton/WishListButton';
import { useCartContext } from '../../lib/context/CartContext/CartContext';

import { useNotification } from '../../lib/context/NotificationContext/NotificationContext';
import { ReactComponent as ShoppingCart } from '../../assets/icons/shopping-cart.svg';
import { ConditionalProductPrice } from '../shared/ConditionalProductPrice/ConditionalProductPrice';

import './ListOfProducts.scss';
import { Quantity } from '../shared/QuantityInput/Quantity';

interface Props {
  big?: boolean;
  product?: ProductProps;
  noWishlistButton?: boolean;
  onRemove?: () => void;
  gridView?: 'ONE' | 'LIST';
}

export const Product = (props: Props) => {
  const history = useHistory();
  const notification = useNotification();
  const sliderImages = getProductImages(props.product);
  const [loading, setLoading] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [selectImg] = useState(sliderImages[0]?.slug);
  const { items, addProduct } = useCartContext();
  const { renderProductFieldsByLanguage } = useProductSingleHelpers();

  // Don't render out-of-stock products
  if (!props.product || 
      props.product.stockStatus === 'OUT_OF_STOCK' || 
      props.product.stockQuantity === 0 ||
      (props.product.type === 'VARIABLE' && 
       (!props.product.variations?.nodes || props.product.variations.nodes.length === 0))) {
    return null;
  }

  const thumbnail = props.big
    ? props.product?.image?.mediaItemUrl
    : props.product?.image?.sourceUrl;

  const salePrice = props.product?.regularPrice
    ? parseFloat(props.product?.salePrice)
    : 0;
  const regularPrice = props.product?.regularPrice
    ? parseFloat(props.product?.regularPrice)
    : 0;

  const discountedPercentage =
    salePrice !== 0 ? ((regularPrice - salePrice) / regularPrice) * 100 : 0;

  const productStockStatus = () => {
    if (!props.product) {
      return;
    }

    if (props.product.stockQuantity) {
      return props.product.stockQuantity;
    }

    if (props.product?.type === 'SIMPLE') {
      return props.product.stockStatus === 'OUT_OF_STOCK' ? 0 : 1;
    }

    const variationsStock = props.product.variations?.nodes?.filter(
      (vs) => vs.stockQuantity && vs.stockQuantity >= 1
    );

    return variationsStock?.length;
  };

  const stockStatus = productStockStatus();

  const img = sliderImages?.find((item: any) => item?.slug === selectImg);

  const stockQuantity = (() => {
    if (props.product?.stockQuantity && props.product.stockQuantity > 0) {
      return props.product.stockQuantity;
    }

    const variationStocks =
      props.product?.variations?.nodes
        ?.map((variation) => variation.stockQuantity || 0)
        .filter((stock) => stock > 0) || [];

    if (variationStocks.length > 0) {
      return Math.max(...variationStocks);
    }

    return props.product?.stock || undefined;
  })();

  const getItemFromCart = () => {
    const cartItem =
      items?.filter(
        (item: any) =>
          item.product.node?.databaseId === props.product?.databaseId
      ) || [];

    if (cartItem.length === 0) {
      return;
    }

    return cartItem[0] as any;
  };

  const handleAddToCart = async () => {
    const cartItem = getItemFromCart();

    if (stockStatus === 0 || !stockStatus) {
      notification.warning('Produkti i zgjedhur nuk është në stok!'); //This product is out of stock.
      return;
    }

    if (
      stockQuantity &&
      cartItem &&
      cartItem.quantity + quantity > stockQuantity
    ) {
      notification.warning(
        `Ju nuk mund ta shtoni atë shumë në shportë, ju tashmë keni ${cartItem?.quantity} sasi në shportën tuaj.`
      );
      return;
    }

    try {
      setLoading(true);
      const id = props.product?.databaseId ?? props.product?.id;

      await addProduct(+id!, quantity, undefined);
    } catch (error) {
      let errors: any = error;
      notification.warning(errors.message, true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`Product${props.big ? ' Product--big' : ''}`}>
      {props.onRemove && <RemoveItemButton onClick={props.onRemove} />}
      <div className="Product__image_and_slider">
        <div className="Product__image">
          <span className="Product__image__holder">
            <ThemeLink to={`/product/${props.product?.slug}`}>
              <Image
                src={
                  img?.sourceUrl ||
                  img?.mediaItemUrl ||
                  thumbnail ||
                  props.product?.thumbnailUrl
                }
                alt={props.product?.name}
              />
            </ThemeLink>
          </span>
          <div className="Product__info">
            {discountedPercentage > 0 && (
              <span className="Product__discount">
                -{discountedPercentage.toFixed()}%
              </span>
            )}
            {/* {stockStatus === 0 || !stockStatus ? (
              <span className="Product__out-of-stock">Jashtë stokut!</span>
            ) : (
              stockQuantity <= 4 && (
                <span className="Product__out-of-stock Product__info-limited">
                  E limituar!
                </span>
              )
            )} */}
          </div>
          {!props.noWishlistButton &&
            (props?.product?.databaseId || props?.product?.id) && (
              <WishListButton
                id={props.product?.databaseId || parseInt(props?.product?.id)}
              />
            )}
        </div>
      </div>
      <div className="Product__content">
        <ThemeLink to={`/product/${props.product?.slug}`}>
          <h4 className="Product__content__title">
            {renderProductFieldsByLanguage(props.product!, 'titleEn', 'name')}
          </h4>
        </ThemeLink>
        <ConditionalProductPrice
          productCategories={props.product?.productCategories}
          regular={props.product?.regularPrice || (props.product as any)?.price}
          sale={props.product?.salePrice || (props.product as any)?.sale_price}
          onSale={props.product?.onSale}
        />
        {/* {(stockQuantity || props.product?.stock) && (
          <span>{stockQuantity || props.product?.stock} në stok</span>
        )} */}
        {props.gridView === 'ONE' && (
          <div
            dangerouslySetInnerHTML={{
              __html: renderProductFieldsByLanguage(
                props.product!,
                'descriptionEn',
                'description'
              )?.toString(),
            }}
          />
        )}
        <div className="Product__content__bottom homeInput">
        <Quantity 
          maxQuantity={stockQuantity}
          quantity={quantity}
          onChange={(value) => {
            setQuantity(value);
          }}
        />
        <button
          id="addToCardBtn"
          className="btn--addtocart btn--addtocart--reverse"
          onClick={handleAddToCart}
          disabled={loading || stockStatus === 0}
        >
          {loading ? (
            <Spinner
              style={{
                color: '#fff',
                width: '1.25rem',
                height: '1.25rem',
                borderWidth: '.2em',
                margin: 'auto',
              }}
            />
          ) : (
            <div className="d-flex justify-content-center align-items-center">
              <ShoppingCart
                style={{ marginRight: 0 }}
                fill={stockStatus === 0 ? '#fff' : '#012db6'}
                width={20}
                height={20}
              />
            </div>
          )}
        </button>
      </div>
      </div>
    </div>
  );
};
